import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Loader2,
  Pause,
  PhoneCall,
  Play,
  Send,
  Settings2,
  MessageCircle,
  History,
  ChevronDown,
  ChevronUp,
  Trash2,
  AlertTriangle,
  Building2,
  Plus,
  Star,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  AgentCallSession,
  AgentV2,
  AnswerHoursDay,
  AnswerMode,
  GreetingMode,
  Persona,
  ReceptionistAccess,
  AccessKey,
  deleteAgent,
  getAgent,
  listAgentSessions,
  normalizeAnswerMode,
  setAgentStatus,
  simulateAgentTurn,
  updateAgent,
} from '../../lib/agentsV2';
import {
  ReceptionistVoice,
  VoiceEngine,
  VoiceGender,
  VoicePreviewSettings,
  fetchVoicePreviews,
  findVoice,
  generateVoicePreview,
  voicesForGender,
} from '../../lib/receptionistVoices';
import { getBusinessProfile } from '../../lib/businessProfiles';
import BusinessProfilePickerModal from '../../components/agents/BusinessProfilePickerModal';

type TabId = 'setup' | 'test' | 'activity';

type ChatMessage = { role: 'user' | 'assistant'; text: string };
type Energy = 'calm' | 'balanced' | 'upbeat';
type FillerStyle = 'off' | 'subtle' | 'chatty';

export default function ReceptionistBuilderPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();

  const [agent, setAgent] = useState<AgentV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>('setup');

  // Setup form state
  const [businessProfileId, setBusinessProfileId] = useState<string | null>(null);
  const [name, setName] = useState('');
  // OpenAI Realtime is the only engine offered in the UI now. ElevenLabs
  // stays fully wired up underneath (config, TwiML, agent-voice function)
  // in case it's re-enabled later.
  const [voiceEngine, setVoiceEngine] = useState<VoiceEngine>('openai');
  const [voiceGender, setVoiceGender] = useState<VoiceGender | null>(null);
  const [voiceId, setVoiceId] = useState('');
  const [openaiVoice, setOpenaiVoice] = useState('');
  const [expressiveness, setExpressiveness] = useState(0.6);
  const [speed, setSpeed] = useState(1.0);
  const [polish, setPolish] = useState(0.75);
  const [persona, setPersona] = useState<Persona>('ai');
  const [collectFields, setCollectFields] = useState<string[]>(['name', 'phone number']);
  const [accessFlags, setAccessFlags] = useState<ReceptionistAccess>({});
  const [energy, setEnergy] = useState<Energy>('balanced');
  const [fillerStyle, setFillerStyle] = useState<FillerStyle>('subtle');
  const [greeting, setGreeting] = useState('');
  const [greetingMode, setGreetingMode] = useState<GreetingMode>('exact');
  const [instructions, setInstructions] = useState('');
  const [answerMode, setAnswerMode] = useState<AnswerMode>('always');
  const [answerHours, setAnswerHours] = useState<Record<string, AnswerHoursDay>>({});
  const [ringSeconds, setRingSeconds] = useState(20);
  // Feedback agent: post-rating actions
  const [fiveStarAction, setFiveStarAction] = useState<'none' | 'google_review'>('none');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [statusBusy, setStatusBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Autosave plumbing: skip the effect run caused by hydrating form state
  // from the loaded agent, and always debounce-call the latest save.
  const hydratedRef = useRef(false);
  const saveRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    if (!agentId) return;
    (async () => {
      try {
        const a = await getAgent(agentId);
        if (!a) {
          navigate('/automations');
          return;
        }
        setAgent(a);
        setName(a.name);
        setBusinessProfileId(a.config.business_profile_id || null);
        // UI is OpenAI-only; a stored 'elevenlabs' engine is migrated to
        // 'openai' on the next autosave.
        setVoiceEngine('openai');
        setVoiceId(a.config.voice || '');
        setOpenaiVoice(a.config.openai_voice || '');
        setVoiceGender(
          a.config.voice_gender
            || findVoice(a.config.voice)?.gender
            || findVoice(a.config.openai_voice, 'openai')?.gender
            || null,
        );
        setExpressiveness(a.config.expressiveness ?? 0.6);
        setSpeed(a.config.speed ?? 1.0);
        setPolish(a.config.polish ?? 0.75);
        setPersona(a.config.persona || 'ai');
        setCollectFields(a.config.collect?.length ? a.config.collect : ['name', 'phone number']);
        setAccessFlags(a.config.access || {});
        setEnergy(a.config.energy || 'balanced');
        setFillerStyle(a.config.filler_style || 'subtle');
        setGreeting(a.config.greeting || '');
        setGreetingMode(a.config.greeting_mode || 'exact');
        setInstructions(a.config.custom_instructions || '');
        setAnswerMode(normalizeAnswerMode(a.config.answer_mode));
        setAnswerHours(a.config.answer_hours || {});
        setRingSeconds(a.config.ring_seconds || 20);
        setFiveStarAction(a.config.five_star_action || 'none');
      } finally {
        setLoading(false);
      }
    })();
  }, [agentId, navigate]);

  const handleSave = useCallback(async () => {
    if (!agent) return;
    setSaveState('saving');
    try {
      const updated = await updateAgent(agent.id, {
        name: name.trim() || (agent.kind === 'feedback' ? 'Feedback Agent' : 'Receptionist'),
        config: {
          ...agent.config,
          voice_engine: voiceEngine,
          voice: voiceId || undefined,
          openai_voice: openaiVoice || undefined,
          voice_gender: voiceGender || undefined,
          expressiveness,
          speed,
          polish,
          persona,
          business_profile_id: businessProfileId || undefined,
          call_goal: 'simple_callback',
          collect: collectFields.length ? collectFields : ['name', 'phone number'],
          access: accessFlags,
          energy,
          filler_style: fillerStyle,
          greeting: greeting.trim(),
          greeting_mode: greetingMode,
          custom_instructions: instructions.trim(),
          answer_mode: answerMode,
          answer_hours: answerHours,
          ring_seconds: ringSeconds,
          five_star_action: fiveStarAction,
        },
      });
      setAgent(updated);
      setSaveState('saved');
    } catch (err) {
      console.error('Failed to save agent:', err);
      setSaveState('error');
    }
  }, [agent, name, businessProfileId, voiceEngine, voiceId, openaiVoice, voiceGender, expressiveness, speed, polish, persona, collectFields, accessFlags, energy, fillerStyle, greeting, greetingMode, instructions, answerMode, answerHours, ringSeconds, fiveStarAction]);

  saveRef.current = handleSave;

  // Live settings: every edit persists automatically shortly after the user
  // stops changing things. No Save button.
  useEffect(() => {
    if (!agent) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    setSaveState('saving');
    const timer = window.setTimeout(() => {
      void saveRef.current();
    }, 600);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id, name, businessProfileId, voiceEngine, voiceId, openaiVoice, voiceGender, expressiveness, speed, polish, persona, collectFields, accessFlags, energy, fillerStyle, greeting, greetingMode, instructions, answerMode, answerHours, ringSeconds, fiveStarAction]);

  const handleToggleLive = useCallback(async () => {
    if (!agent || statusBusy) return;
    setStatusBusy(true);
    try {
      // Flush any just-made edits so what goes live is what's on screen.
      await handleSave();
      const next = agent.status === 'live' ? 'draft' : 'live';
      await setAgentStatus(agent, next);
      setAgent({ ...agent, status: next });
      setBanner(agent.kind === 'feedback'
        ? (next === 'live'
          ? 'Feedback agent is live — automations can now deploy it to call customers.'
          : 'Feedback agent set to draft — no feedback calls will be placed.')
        : (next === 'live'
          ? 'Receptionist is live — it now answers calls to your business number.'
          : 'Receptionist set to draft — calls use your normal forwarding/voicemail.'));
      setTimeout(() => setBanner(null), 4000);
    } catch (err) {
      console.error('Failed to change status:', err);
      setBanner('Could not change status — please try again');
    } finally {
      setStatusBusy(false);
    }
  }, [agent, statusBusy, handleSave]);

  const handleDelete = useCallback(async () => {
    if (!agent || deleting) return;
    setDeleting(true);
    try {
      await deleteAgent(agent.id);
      navigate('/automations');
    } catch (err) {
      console.error('Failed to delete agent:', err);
      setDeleting(false);
      setConfirmDelete(false);
      setBanner('Could not delete the agent — please try again');
      setTimeout(() => setBanner(null), 4000);
    }
  }, [agent, deleting, navigate]);

  if (loading || !agent) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const isLive = agent.status === 'live';

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200/60 px-4 sm:px-6 py-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/automations')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 ${agent.kind === 'feedback' ? 'bg-amber-500' : 'bg-blue-600'}`}>
              {agent.kind === 'feedback'
                ? <Star className="h-4.5 w-4.5 text-white" />
                : <PhoneCall className="h-4.5 w-4.5 text-white" />}
            </div>
            <div className="min-w-0">
              <h1 className="text-[17px] font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                {name || (agent.kind === 'feedback' ? 'Feedback Agent' : 'Receptionist')}
              </h1>
              <p className="text-xs text-gray-500">{agent.kind === 'feedback' ? 'AI Feedback Agent' : 'AI Receptionist'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {saveState === 'saving' && (
              <span className="text-[13px] text-gray-400 flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </span>
            )}
            {saveState === 'saved' && (
              <span className="text-[13px] text-gray-400 flex items-center gap-1">
                <Check className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
            {saveState === 'error' && (
              <button
                onClick={handleSave}
                className="text-[13px] font-semibold text-red-600 hover:text-red-700"
              >
                Save failed — retry
              </button>
            )}
            <button
              onClick={handleToggleLive}
              disabled={statusBusy}
              className={`px-3.5 py-2 rounded-[10px] text-sm font-semibold transition-colors disabled:opacity-60 flex items-center gap-2 ${
                isLive
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500' : 'bg-white/70'}`} />
              {statusBusy ? 'Updating…' : isLive ? 'Live' : 'Go live'}
            </button>
          </div>
        </div>
        {banner && (
          <div className="max-w-3xl mx-auto mt-2">
            <p className="text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-[10px] px-3 py-2">
              {banner}
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4">
        <div className="flex gap-1 bg-gray-200/60 rounded-[12px] p-1 w-fit">
          {([
            { id: 'setup', label: 'Setup', icon: Settings2 },
            { id: 'test', label: 'Test', icon: MessageCircle },
            { id: 'activity', label: 'Activity', icon: History },
          ] as const).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-[10px] text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                tab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 pb-24">
        {tab === 'setup' && (
          <>
          <SetupTab
            businessId={agent.business_id}
            agentId={agent.id}
            kind={agent.kind}
            businessProfileId={businessProfileId}
            name={name}
            voiceEngine={voiceEngine}
            voiceGender={voiceGender}
            voiceId={voiceId}
            openaiVoice={openaiVoice}
            expressiveness={expressiveness}
            speed={speed}
            polish={polish}
            persona={persona}
            collectFields={collectFields}
            accessFlags={accessFlags}
            energy={energy}
            fillerStyle={fillerStyle}
            greeting={greeting}
            greetingMode={greetingMode}
            instructions={instructions}
            answerMode={answerMode}
            answerHours={answerHours}
            ringSeconds={ringSeconds}
            fiveStarAction={fiveStarAction}
            onChange={(patch) => {
              if (patch.name !== undefined) setName(patch.name);
              if (patch.businessProfileId !== undefined) setBusinessProfileId(patch.businessProfileId);
              if (patch.voiceEngine !== undefined) setVoiceEngine(patch.voiceEngine);
              if (patch.voiceGender !== undefined) setVoiceGender(patch.voiceGender);
              if (patch.voiceId !== undefined) setVoiceId(patch.voiceId);
              if (patch.openaiVoice !== undefined) setOpenaiVoice(patch.openaiVoice);
              if (patch.expressiveness !== undefined) setExpressiveness(patch.expressiveness);
              if (patch.speed !== undefined) setSpeed(patch.speed);
              if (patch.polish !== undefined) setPolish(patch.polish);
              if (patch.persona !== undefined) setPersona(patch.persona);
              if (patch.collectFields !== undefined) setCollectFields(patch.collectFields);
              if (patch.accessFlags !== undefined) setAccessFlags(patch.accessFlags);
              if (patch.energy !== undefined) setEnergy(patch.energy);
              if (patch.fillerStyle !== undefined) setFillerStyle(patch.fillerStyle);
              if (patch.greeting !== undefined) setGreeting(patch.greeting);
              if (patch.greetingMode !== undefined) setGreetingMode(patch.greetingMode);
              if (patch.instructions !== undefined) setInstructions(patch.instructions);
              if (patch.answerMode !== undefined) setAnswerMode(patch.answerMode);
              if (patch.answerHours !== undefined) setAnswerHours(patch.answerHours);
              if (patch.ringSeconds !== undefined) setRingSeconds(patch.ringSeconds);
              if (patch.fiveStarAction !== undefined) setFiveStarAction(patch.fiveStarAction);
            }}
          />
          <div className="mt-5 bg-white rounded-2xl border border-red-200 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-900">Delete this agent</p>
                <p className="text-[13px] text-gray-500 mt-0.5 leading-snug">
                  Permanently removes the agent and every setting — voice, greeting, persona, call goal, answering rules. Past call activity is kept.
                </p>
              </div>
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] text-[13px] font-semibold text-red-600 border border-red-300 hover:bg-red-50 transition-colors flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
                Delete agent
              </button>
            </div>
          </div>
          </>
        )}
        {tab === 'test' && <TestTab agentId={agent.id} />}
        {tab === 'activity' && <ActivityTab agentId={agent.id} />}
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => !deleting && setConfirmDelete(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 text-center mt-4">
              Delete "{agent.name || 'Receptionist'}"?
            </h2>
            <p className="text-sm text-gray-600 text-center mt-2 leading-relaxed">
              This permanently deletes the agent. <span className="font-semibold text-gray-900">All settings will be lost</span> — voice, greeting, persona, call goal, and answering rules — and this can't be undone.
              {isLive && (
                <span className="block mt-2 font-semibold text-red-600">
                  This agent is live right now. It will stop answering calls immediately.
                </span>
              )}
            </p>
            <p className="text-[13px] text-gray-500 text-center mt-2">
              Past call activity and transcripts are kept.
            </p>
            <div className="flex gap-2.5 mt-5">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-[10px] text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Keep agent
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-[10px] text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const WEEK_DAYS: Array<{ key: string; label: string }> = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const COLLECT_PRESETS = [
  'name',
  'phone number',
  'email',
  'reason for calling',
  'company name',
  'preferred callback time',
];

type AccessOption = {
  key: AccessKey;
  group: 'caller' | 'business';
  title: string;
  desc: string;
};

const ACCESS_OPTIONS: AccessOption[] = [
  {
    key: 'customer_profile',
    group: 'caller',
    title: 'Customer profile',
    desc: 'Email, address, owner notes, tags, total spend, and store credit for the matched caller.',
  },
  {
    key: 'vehicles',
    group: 'caller',
    title: 'Vehicles on file',
    desc: 'Their cars — make, model, year, color — so it can say "the usual on the Model 3?"',
  },
  {
    key: 'bookings',
    group: 'caller',
    title: 'Bookings',
    desc: 'Upcoming appointments and booking history, so "when\'s my appointment?" gets a real answer.',
  },
  {
    key: 'invoices',
    group: 'caller',
    title: 'Invoices & payments',
    desc: 'Outstanding balances and recent payments. Only discussed if the caller brings up billing.',
  },
  {
    key: 'proposals',
    group: 'caller',
    title: 'Proposals & quotes',
    desc: 'Recent proposals and their status, for "did you get my quote?" calls.',
  },
  {
    key: 'crm_leads',
    group: 'caller',
    title: 'CRM leads',
    desc: 'Open leads in your pipeline, so it knows this caller already reached out.',
  },
  {
    key: 'messages',
    group: 'caller',
    title: 'Message history',
    desc: 'Recent SMS and email threads with this customer, for continuity from texting to calling.',
  },
  {
    key: 'call_history',
    group: 'caller',
    title: 'Call history',
    desc: 'Past phone calls with your business, including summaries of their previous calls with the receptionist.',
  },
  {
    key: 'memberships',
    group: 'caller',
    title: 'Memberships & subscriptions',
    desc: 'Active recurring plans, so members get recognized as members.',
  },
  {
    key: 'gift_cards',
    group: 'caller',
    title: 'Gift cards',
    desc: 'Gift cards they purchased and remaining balances.',
  },
  {
    key: 'projects',
    group: 'caller',
    title: 'Projects & field quotes',
    desc: 'Open projects and scheduled on-site quote visits.',
  },
  {
    key: 'pricing_details',
    group: 'business',
    title: 'Detailed pricing',
    desc: 'Real price options (sizes, tiers) and add-ons per service — not just base prices — so quotes are accurate.',
  },
  {
    key: 'service_areas',
    group: 'business',
    title: 'Service areas',
    desc: 'The areas and postal codes you cover, so it can answer "do you come out to my area?"',
  },
];

function AccessToggleRow({
  option,
  enabled,
  onToggle,
}: {
  option: AccessOption;
  enabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-[14px] font-semibold text-gray-900">{option.title}</p>
        <p className="text-[13px] text-gray-500 leading-snug mt-0.5">{option.desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onToggle(!enabled)}
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
          enabled ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            enabled ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}

function CollectFieldsEditor({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (fields: string[]) => void;
}) {
  const [custom, setCustom] = useState('');

  const toggle = (field: string) => {
    onChange(
      selected.includes(field)
        ? selected.filter((f) => f !== field)
        : [...selected, field],
    );
  };

  const addCustom = () => {
    const field = custom.trim().toLowerCase();
    setCustom('');
    if (!field || selected.includes(field)) return;
    onChange([...selected, field]);
  };

  // Presets first, then any custom fields the user added.
  const chips = [...COLLECT_PRESETS, ...selected.filter((f) => !COLLECT_PRESETS.includes(f))];

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {chips.map((field) => {
          const active = selected.includes(field);
          return (
            <button
              key={field}
              type="button"
              onClick={() => toggle(field)}
              className={`px-3 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${
                active
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
              }`}
            >
              {active ? '✓ ' : '+ '}{field}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2.5">
        <input
          className="flex-1 rounded-[10px] border border-gray-300 px-3 py-1.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 bg-white"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="Add your own, e.g. vehicle make and model"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!custom.trim()}
          className="px-3.5 py-1.5 rounded-[10px] text-[13px] font-semibold bg-gray-900 text-white disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
}

function SetupTab({
  businessId,
  agentId,
  kind,
  businessProfileId,
  name,
  voiceEngine,
  voiceGender,
  voiceId,
  openaiVoice,
  expressiveness,
  speed,
  polish,
  persona,
  collectFields,
  accessFlags,
  energy,
  fillerStyle,
  greeting,
  greetingMode,
  instructions,
  answerMode,
  answerHours,
  ringSeconds,
  fiveStarAction,
  onChange,
}: {
  businessId: string;
  agentId: string;
  kind: string;
  businessProfileId: string | null;
  name: string;
  voiceEngine: VoiceEngine;
  voiceGender: VoiceGender | null;
  voiceId: string;
  openaiVoice: string;
  expressiveness: number;
  speed: number;
  polish: number;
  persona: Persona;
  collectFields: string[];
  accessFlags: ReceptionistAccess;
  energy: Energy;
  fillerStyle: FillerStyle;
  greeting: string;
  greetingMode: GreetingMode;
  instructions: string;
  answerMode: AnswerMode;
  answerHours: Record<string, AnswerHoursDay>;
  ringSeconds: number;
  fiveStarAction: 'none' | 'google_review';
  onChange: (patch: {
    name?: string;
    businessProfileId?: string | null;
    voiceEngine?: VoiceEngine;
    voiceGender?: VoiceGender;
    voiceId?: string;
    openaiVoice?: string;
    expressiveness?: number;
    speed?: number;
    polish?: number;
    persona?: Persona;
    collectFields?: string[];
    accessFlags?: ReceptionistAccess;
    energy?: Energy;
    fillerStyle?: FillerStyle;
    greeting?: string;
    greetingMode?: GreetingMode;
    instructions?: string;
    answerMode?: AnswerMode;
    answerHours?: Record<string, AnswerHoursDay>;
    ringSeconds?: number;
    fiveStarAction?: 'none' | 'google_review';
  }) => void;
}) {
  const field = 'w-full rounded-[10px] border border-gray-300 px-3.5 py-2.5 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 bg-white';
  const label = 'block text-sm font-semibold text-gray-900 mb-1.5';
  const hint = 'text-[13px] text-gray-500 mt-1.5 leading-snug';

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileName, setProfileName] = useState<string | null>(null);
  // Feedback agent: the Google review link is businesses.google_review_url —
  // the SAME field the feedback settings (Customize form) edit. Editing it
  // here updates it there and vice versa; there is no agent-level copy.
  const [businessReviewUrl, setBusinessReviewUrl] = useState('');
  const reviewUrlTimer = useRef<number | null>(null);
  // The agent's calls are skipped while the feedback system is off, so the
  // builder shows a warning banner whenever that's the case.
  const [feedbackSystemOn, setFeedbackSystemOn] = useState<boolean | null>(null);

  useEffect(() => {
    if (kind !== 'feedback' || !businessId) return;
    let cancelled = false;
    supabase
      .from('businesses')
      .select('google_review_url, feedback_enabled')
      .eq('id', businessId)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        setBusinessReviewUrl(data?.google_review_url || '');
        setFeedbackSystemOn(data?.feedback_enabled !== false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, businessId]);

  const updateReviewUrl = (value: string) => {
    setBusinessReviewUrl(value);
    if (reviewUrlTimer.current) window.clearTimeout(reviewUrlTimer.current);
    reviewUrlTimer.current = window.setTimeout(() => {
      supabase
        .from('businesses')
        .update({ google_review_url: value.trim() })
        .eq('id', businessId)
        .then(({ error }) => {
          if (error) console.error('Failed to save Google review link:', error);
        });
    }, 600);
  };

  // Resolve the assigned profile's display name (modal selections set it
  // directly; this covers the initial load and stale ids).
  useEffect(() => {
    if (!businessProfileId) {
      setProfileName(null);
      return;
    }
    let cancelled = false;
    getBusinessProfile(businessProfileId)
      .then((p) => {
        if (!cancelled) setProfileName(p ? p.name : null);
      })
      .catch(() => {
        if (!cancelled) setProfileName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [businessProfileId]);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 space-y-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
        <div>
          <label className={label}>Business profile</label>
          {businessProfileId ? (
            <button
              type="button"
              onClick={() => setProfileModalOpen(true)}
              className="w-full flex items-center gap-3 p-3 rounded-[10px] border border-blue-200 bg-blue-50/40 hover:bg-blue-50 transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-[10px] bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-gray-900 truncate">
                  {profileName ?? 'Business profile'}
                </div>
                <div className="text-[13px] text-gray-500">Grounds the agent in your organization</div>
              </div>
              <span className="text-[13px] font-semibold text-blue-600 flex-shrink-0">Change</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setProfileModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-[10px] border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors text-sm font-semibold"
            >
              <Plus className="h-4 w-4" />
              Add business profile
            </button>
          )}
          <p className={hint}>
            Optional — tells the agent who you are and what you do, so it represents the organization accurately. Leave blank and it works as normal.
          </p>
          <BusinessProfilePickerModal
            isOpen={profileModalOpen}
            onClose={() => setProfileModalOpen(false)}
            businessId={businessId}
            selectedProfileId={businessProfileId}
            onSelect={(profile) => {
              setProfileName(profile ? profile.name : null);
              onChange({ businessProfileId: profile ? profile.id : null });
            }}
          />
        </div>
        <div>
          <label className={label}>Agent name</label>
          <input
            className={field}
            value={name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Receptionist"
          />
        </div>
        {/* The feedback agent places outbound calls via automations — it
            never answers the phone, so answering rules don't apply. */}
        {kind !== 'feedback' && (
        <div>
          <label className={label}>When should it answer?</label>
          <select
            className={field}
            value={answerMode}
            onChange={(e) => onChange({ answerMode: e.target.value as AnswerMode })}
          >
            <option value="always">Always — answers every incoming call immediately</option>
            <option value="missed_calls">When calls are missed — answers if nobody picks up</option>
            <option value="after_hours">After hours — answers only during the hours you set</option>
          </select>
          {answerMode === 'missed_calls' && (
            <div className="mt-2.5 p-3 rounded-[10px] bg-blue-50/60 border border-blue-100">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">Ring my phone for</span>
                <select
                  value={ringSeconds}
                  onChange={(e) => onChange({ ringSeconds: Number(e.target.value) })}
                  className="rounded-[8px] border border-gray-300 px-2 py-1.5 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                >
                  {[10, 15, 20, 25, 30].map((s) => (
                    <option key={s} value={s}>{s} seconds</option>
                  ))}
                </select>
              </div>
              <p className="text-[13px] text-gray-500 mt-1.5 leading-snug">
                When you answer a forwarded call, you'll hear "press any key to accept." That keeps your cell phone's voicemail from taking the call — if you don't accept, the receptionist picks up instead, no matter how your carrier behaves.
              </p>
            </div>
          )}
          {answerMode === 'after_hours' && (
            <div className="mt-2.5">
              <p className="text-[13px] text-gray-500 mb-2">
                Toggle a day on and set when the receptionist answers. An end time earlier than the start runs overnight (for example five PM to eight AM).
              </p>
              <div className="space-y-1.5">
                {WEEK_DAYS.map(({ key, label: dayLabel }) => {
                  const day = answerHours[key] || {};
                  const enabled = Boolean(day.enabled);
                  const setDay = (patch: AnswerHoursDay) =>
                    onChange({
                      answerHours: {
                        ...answerHours,
                        [key]: { start: '17:00', end: '08:00', ...day, ...patch },
                      },
                    });
                  return (
                    <div
                      key={key}
                      className={`flex items-center gap-3 p-2.5 rounded-[10px] border ${
                        enabled ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200/60 bg-gray-50'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setDay({ enabled: !enabled })}
                        className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                          enabled ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        aria-label={`Toggle ${dayLabel}`}
                      >
                        <span
                          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                            enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <span className={`text-sm font-semibold w-24 flex-shrink-0 ${enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                        {dayLabel}
                      </span>
                      {enabled ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <input
                            type="time"
                            value={day.start || '17:00'}
                            onChange={(e) => setDay({ start: e.target.value })}
                            className="rounded-[8px] border border-gray-300 px-2 py-1.5 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                          />
                          <span className="text-xs text-gray-500">to</span>
                          <input
                            type="time"
                            value={day.end || '08:00'}
                            onChange={(e) => setDay({ end: e.target.value })}
                            className="rounded-[8px] border border-gray-300 px-2 py-1.5 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Off</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}
        <div>
          <label className={label}>Define your agent</label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => onChange({ persona: 'ai' })}
              className={`text-left p-3 rounded-[10px] border transition-colors ${
                persona === 'ai'
                  ? 'border-blue-500 bg-blue-50/40'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="text-[14px] font-semibold text-gray-900 block">AI receptionist</span>
              <span className="text-[12px] text-gray-500 leading-snug block mt-0.5">Polished and professional.</span>
            </button>
            <button
              type="button"
              onClick={() => onChange({ persona: 'human' })}
              className={`text-left p-3 rounded-[10px] border transition-colors ${
                persona === 'human'
                  ? 'border-blue-500 bg-blue-50/40'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="text-[14px] font-semibold text-gray-900 block">Human-like</span>
              <span className="text-[12px] text-gray-500 leading-snug block mt-0.5">Indistinguishable from a real person.</span>
            </button>
          </div>
        </div>
        <div>
          <label className={label}>Gender</label>
          <div className="flex gap-2">
            {(['female', 'male'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => {
                  if (g === voiceGender) return;
                  // Clear voices when switching so a mismatched voice
                  // never lingers behind the scenes.
                  onChange({ voiceGender: g, voiceId: '', openaiVoice: '' });
                }}
                className={`flex-1 px-4 py-2.5 rounded-[10px] text-[15px] font-semibold border transition-colors ${
                  voiceGender === g
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {g === 'female' ? 'Female' : 'Male'}
              </button>
            ))}
          </div>
          {!voiceGender && (
            <p className={hint}>Pick a gender to see the available voices.</p>
          )}
        </div>
        {voiceGender && (
          <div>
            <label className={label}>Voice</label>
            <VoicePicker
              engine={voiceEngine}
              gender={voiceGender}
              voiceId={voiceEngine === 'openai' ? openaiVoice : voiceId}
              settings={{ engine: voiceEngine, expressiveness, speed, polish }}
              onSelect={(id) =>
                onChange(voiceEngine === 'openai' ? { openaiVoice: id } : { voiceId: id })
              }
            />
            <p className={hint}>
              {voiceEngine === 'openai'
                ? 'Tap play to hear the voice. Real calls use OpenAI\u2019s speech-to-speech engine, which sounds even more natural than these samples.'
                : 'Tap play to hear a sample. Samples use all the tuning sliders below, and they\u2019re exactly what callers hear on the phone — tune everything by ear.'}
            </p>
            <div className="mt-4 space-y-4">
              {voiceEngine === 'elevenlabs' && (
                <VoiceSlider
                  label="Expressiveness"
                  value={expressiveness}
                  display={`${Math.round(expressiveness * 100)}%`}
                  min={0}
                  max={0.85}
                  step={0.05}
                  leftLabel="Steady & even"
                  rightLabel="Lively & human"
                  onChange={(v) => onChange({ expressiveness: v })}
                  hint="How much the voice varies its energy, pitch, and pacing between sentences. Higher is less like a recording; too high can sound over-acted."
                />
              )}
              <VoiceSlider
                label="Speaking speed"
                value={speed}
                display={`${speed.toFixed(2)}x`}
                min={0.7}
                max={1.2}
                step={0.05}
                leftLabel="Slower"
                rightLabel="Faster"
                onChange={(v) => onChange({ speed: v })}
                hint="Real receptionists rarely talk at a perfectly even clip — a touch above or below 1x often feels more natural than dead center."
              />
              {voiceEngine === 'elevenlabs' && (
                <VoiceSlider
                  label="Polish"
                  value={polish}
                  display={`${Math.round(polish * 100)}%`}
                  min={0.3}
                  max={1}
                  step={0.05}
                  leftLabel="Loose & natural"
                  rightLabel="Crisp & studio"
                  onChange={(v) => onChange({ polish: v })}
                  hint="How tightly the voice sticks to its polished studio sound. Lowering it loosens the delivery and helps kill the 'perfect recording' feel; too low can introduce artifacts."
                />
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Energy</label>
            <select
              className={field}
              value={energy}
              onChange={(e) => onChange({ energy: e.target.value as Energy })}
            >
              <option value="calm">Calm — relaxed and unhurried</option>
              <option value="balanced">Balanced — friendly and warm</option>
              <option value="upbeat">Upbeat — energetic, smiling</option>
            </select>
          </div>
          {/* Human-like manages its own hesitations and pacing — the filler
              setting is ignored by that persona, so it's hidden. */}
          {persona !== 'human' && (
            <div>
              <label className={label}>Filler words</label>
              <select
                className={field}
                value={fillerStyle}
                onChange={(e) => onChange({ fillerStyle: e.target.value as FillerStyle })}
              >
                <option value="off">Off — clean and direct</option>
                <option value="subtle">Subtle — occasional "um", "let me see"</option>
                <option value="chatty">Chatty — casual, conversational asides</option>
              </select>
            </div>
          )}
          <p className={`${hint} sm:col-span-2 !mt-0`}>
            {persona === 'human'
              ? 'Energy sets the overall mood. Hesitations, stumbles, and pacing are handled by the Human-like profile itself.'
              : 'These shape how the receptionist words things on real calls and in the Test tab — natural hesitations and tone go a long way toward sounding human.'}
          </p>
        </div>
        <div>
          <label className={label}>Greeting</label>
          <select
            className={field}
            value={greetingMode}
            onChange={(e) => onChange({ greetingMode: e.target.value as GreetingMode })}
          >
            <option value="auto">AI decides — fresh greeting every call</option>
            <option value="guided">Guided — you give a guideline, AI phrases it</option>
            <option value="exact">Exact script — word for word, every call</option>
          </select>
          {greetingMode !== 'auto' && (
            <div className="mt-3">
              <textarea
                className={`${field} resize-none`}
                rows={2}
                value={greeting}
                onChange={(e) => onChange({ greeting: e.target.value })}
                placeholder={greetingMode === 'guided'
                  ? 'e.g. Mention we\u2019re booking fall details right now and ask what their car needs.'
                  : "Thanks for calling! I'm the virtual receptionist. How can I help you today?"}
              />
              <p className={hint}>
                {greetingMode === 'guided'
                  ? 'What the greeting should get across. The AI works it in naturally without reciting it.'
                  : kind === 'feedback'
                    ? 'The first thing the customer hears when they answer your call. Leave blank for a default that uses your business name.'
                    : 'The first thing callers hear when the receptionist picks up. Leave blank for a default that uses your business name.'}
              </p>
            </div>
          )}
        </div>
        <div>
          <label className={label}>Instructions</label>
          <textarea
            className={`${field} resize-none`}
            rows={5}
            value={instructions}
            onChange={(e) => onChange({ instructions: e.target.value })}
            placeholder={'Anything the receptionist should know or do. For example:\nWe don\'t detail boats or RVs.\nCeramic coating quotes require seeing the car in person.'}
          />
          <p className={hint}>
            The receptionist already knows your business name, hours, and services. Add rules, pricing notes, or answers to common questions here.
          </p>
        </div>
      </div>

      {kind !== 'feedback' ? (
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
        <label className={label}>Call goal</label>
        <p className={`${hint} !mt-0 mb-3`}>What the receptionist is trying to accomplish on every call.</p>
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-3 rounded-[10px] border border-blue-500 bg-blue-50/40 cursor-pointer">
            <input type="radio" className="mt-1" checked readOnly />
            <span>
              <span className="text-[15px] font-semibold text-gray-900 block">Simple call back</span>
              <span className="text-[13px] text-gray-500">Answers questions, then gathers the caller's details so a real person can call them back. Nothing gets booked or promised on the call.</span>
            </span>
          </label>
          <div className="flex items-start gap-3 p-3 rounded-[10px] border border-gray-200 opacity-55">
            <input type="radio" className="mt-1" disabled />
            <span>
              <span className="text-[15px] font-semibold text-gray-900 block">Full agent <span className="ml-1 text-[11px] font-semibold uppercase tracking-wide text-blue-600">Coming soon</span></span>
              <span className="text-[13px] text-gray-500">Creates real bookings, answers deep questions, dispatches support.</span>
            </span>
          </div>
        </div>
        <div className="mt-4">
          <label className={label}>Info to collect for the call back</label>
          <CollectFieldsEditor
            selected={collectFields}
            onChange={(fields) => onChange({ collectFields: fields })}
          />
          <p className={hint}>
            The receptionist works these in naturally over the conversation — one at a time, confirming numbers and emails back — and checks it has everything before hanging up.
          </p>
        </div>
      </div>
      ) : (
      <>
      {feedbackSystemOn === false && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900">Feedback system is turned off</p>
            <p className="text-[13px] text-red-800 mt-0.5 leading-snug">
              This agent saves ratings into your feedback system, so its calls are skipped while it's off — even when the agent is live. Turn it on in Settings → Feedback.
            </p>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
        <label className={label}>Call goal</label>
        <p className={`${hint} !mt-0 mb-3`}>What the feedback agent is trying to accomplish on every call.</p>
        <div className="flex items-start gap-3 p-3 rounded-[10px] border border-amber-400 bg-amber-50/40">
          <Star className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <span>
            <span className="text-[15px] font-semibold text-gray-900 block">Collect feedback</span>
            <span className="text-[13px] text-gray-500">
              Calls the customer after their booking, asks how it went, and gets one overall rating out of five stars plus any comments. The rating is applied to every question on your feedback form and lands in your feedback reports like a normal submission.
            </span>
          </span>
        </div>
        <p className={hint}>
          Deployed by an automation (for example, when a booking is completed). It uses the feedback questions you've set up in Settings → Feedback.
        </p>
      </div>
      </>
      )}

      {kind === 'feedback' && (
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 space-y-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
        <div>
          <label className={label}>After the rating</label>
          <p className={`${hint} !mt-0`}>What the agent does once it has the customer's stars.</p>
        </div>
        <div>
          <label className={label}>5-star rating</label>
          <select
            className={field}
            value={fiveStarAction}
            onChange={(e) => onChange({ fiveStarAction: e.target.value as 'none' | 'google_review' })}
          >
            <option value="none">Do nothing — just record the feedback</option>
            <option value="google_review">Ask for a Google review &amp; text them the link</option>
          </select>
          {fiveStarAction === 'google_review' && (
            <div className="mt-3">
              <input
                type="url"
                className={field}
                value={businessReviewUrl}
                onChange={(e) => updateReviewUrl(e.target.value)}
                placeholder="https://g.page/r/…/review"
              />
              <p className={hint}>
                Shared with your feedback settings (Settings → Feedback → Customize form) — changing it here changes it there too.
              </p>
              {!businessReviewUrl.trim() && (
                <p className="text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-[10px] px-3 py-2 mt-2">
                  No review link set — the agent won't ask for a review until you add one.
                </p>
              )}
              <p className={hint}>
                On a five-star call the agent asks if they'd mind leaving a quick Google review. If they say yes, the link is texted to them automatically right after the call — you'll see it in Messages.
              </p>
            </div>
          )}
        </div>
        <div>
          <label className={label}>Less than 5 stars</label>
          <div className="flex items-start gap-3 p-3 rounded-[10px] border border-gray-200 opacity-55">
            <input type="radio" className="mt-1" disabled />
            <span>
              <span className="text-[15px] font-semibold text-gray-900 block">Create support ticket <span className="ml-1 text-[11px] font-semibold uppercase tracking-wide text-blue-600">Coming soon</span></span>
              <span className="text-[13px] text-gray-500">Automatically opens a ticket so the team can follow up on unhappy feedback. For now, low ratings are recorded with the customer's comments in the call summary.</span>
            </span>
          </div>
        </div>
      </div>
      )}

      {kind === 'feedback' && (
        <FeedbackTestCallCard businessId={businessId} agentId={agentId} />
      )}

      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <label className={label}>Data access</label>
            <p className={`${hint} !mt-0.5 mb-1`}>
              {kind === 'feedback'
                ? 'The feedback agent always knows the booking it\u2019s calling about — that context is built in. Each source you grant here gives it extra background on the customer for a more personal call.'
                : 'Everything is off by default — the receptionist starts as a pure callback tool that knows nothing about the caller. Each source you grant lets it recognize callers and answer real questions about their account.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const allOn = ACCESS_OPTIONS.every((o) => accessFlags[o.key]);
              const next: ReceptionistAccess = {};
              if (!allOn) for (const o of ACCESS_OPTIONS) next[o.key] = true;
              onChange({ accessFlags: next });
            }}
            className="shrink-0 text-[13px] font-medium text-blue-600 hover:text-blue-700 mt-0.5"
          >
            {ACCESS_OPTIONS.every((o) => accessFlags[o.key]) ? 'Turn all off' : 'Grant full access'}
          </button>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mt-4 mb-1">About the caller</p>
        <div className="divide-y divide-gray-100">
          {ACCESS_OPTIONS.filter((o) => o.group === 'caller').map((o) => (
            <AccessToggleRow
              key={o.key}
              option={o}
              enabled={!!accessFlags[o.key]}
              onToggle={(on) => onChange({ accessFlags: { ...accessFlags, [o.key]: on } })}
            />
          ))}
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mt-5 mb-1">About the business</p>
        <div className="divide-y divide-gray-100">
          {ACCESS_OPTIONS.filter((o) => o.group === 'business').map((o) => (
            <AccessToggleRow
              key={o.key}
              option={o}
              enabled={!!accessFlags[o.key]}
              onToggle={(on) => onChange({ accessFlags: { ...accessFlags, [o.key]: on } })}
            />
          ))}
        </div>
      </div>

    </div>
  );
}

/**
 * Feedback-agent test call: rings any number you enter and runs the real
 * outbound flow end to end (same edge function the automation uses). Works
 * on drafts, so the agent can be tuned before going live. Test calls have
 * no booking attached, so nothing is written to your feedback data.
 */
function FeedbackTestCallCard({ businessId, agentId }: { businessId: string; agentId: string }) {
  const [phone, setPhone] = useState('');
  const [calling, setCalling] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const placeCall = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10 || calling) return;
    setCalling(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('agent-feedback-call', {
        body: { business_id: businessId, agent_id: agentId, to: phone.trim() },
      });
      if (error || !data?.success) {
        const reason = data?.error === 'insufficient_tokens'
          ? 'Not enough AI tokens — top up on the AI Settings page.'
          : 'Could not place the call. Check your Twilio settings and try again.';
        setResult({ ok: false, text: reason });
      } else {
        setResult({ ok: true, text: 'Calling now — pick up and talk to your agent.' });
      }
    } catch (err) {
      console.error('Feedback test call failed:', err);
      setResult({ ok: false, text: 'Could not place the call. Please try again.' });
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
      <label className="block text-sm font-semibold text-gray-900 mb-1.5">Test call</label>
      <p className="text-[13px] text-gray-500 leading-snug mb-3">
        Enter your number and the agent will call you like a real customer — same voice, same flow. Test calls aren't linked to a booking, so no feedback gets recorded.
      </p>
      <div className="flex gap-2">
        <input
          type="tel"
          className="flex-1 rounded-[10px] border border-gray-300 px-3.5 py-2.5 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 bg-white"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 555 123 4567"
        />
        <button
          type="button"
          onClick={placeCall}
          disabled={calling || phone.replace(/\D/g, '').length < 10}
          className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {calling ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
          {calling ? 'Calling…' : 'Call me'}
        </button>
      </div>
      {result && (
        <p className={`text-[13px] mt-2 ${result.ok ? 'text-green-700' : 'text-red-600'}`}>
          {result.text}
        </p>
      )}
    </div>
  );
}

function VoiceSlider({
  label,
  value,
  display,
  min,
  max,
  step,
  leftLabel,
  rightLabel,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  leftLabel: string;
  rightLabel: string;
  onChange: (value: number) => void;
  hint: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className="text-[13px] text-gray-500">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-blue-600"
      />
      <div className="flex justify-between text-[12px] text-gray-400 mt-0.5">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      <p className="text-[13px] text-gray-500 mt-1 leading-snug">{hint}</p>
    </div>
  );
}

function VoicePicker({
  engine,
  gender,
  voiceId,
  settings,
  onSelect,
}: {
  engine: VoiceEngine;
  gender: VoiceGender;
  voiceId: string;
  settings: VoicePreviewSettings;
  onSelect: (id: string) => void;
}) {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [playedFallback, setPlayedFallback] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Invalidates in-flight preview requests when the user clicks elsewhere.
  const requestRef = useRef(0);

  useEffect(() => {
    if (engine !== 'elevenlabs') return; // stock fallbacks only exist for ElevenLabs
    let alive = true;
    fetchVoicePreviews().then((map) => {
      if (alive) setPreviews(map);
    });
    return () => {
      alive = false;
    };
  }, [engine]);

  const stopAudio = useCallback(() => {
    requestRef.current += 1;
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingId(null);
    setLoadingId(null);
  }, []);

  // Stop playback when switching engine/gender or leaving the tab, and cut
  // any playing sample the moment a voice slider moves so the next play
  // renders with the new settings instead of finishing the stale one.
  useEffect(() => stopAudio, [engine, gender, settings.expressiveness, settings.speed, settings.polish, stopAudio]);

  const stockUrlFor = (v: ReceptionistVoice) =>
    engine === 'elevenlabs' ? previews[v.id] || v.previewUrl : undefined;

  const togglePlay = async (v: ReceptionistVoice) => {
    if (playingId === v.id || loadingId === v.id) {
      stopAudio();
      return;
    }
    stopAudio();
    const requestId = ++requestRef.current;

    // Prefer a live sample rendered with the agent's current voice settings;
    // fall back to the stock recording when generation isn't available.
    setLoadingId(v.id);
    const dynamicUrl = await generateVoicePreview(v.id, settings);
    if (requestRef.current !== requestId) return; // superseded by another click
    setLoadingId(null);
    setPlayedFallback(!dynamicUrl);

    const url = dynamicUrl || stockUrlFor(v);
    if (!url) return;

    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingId(v.id);
    audio.onended = () => setPlayingId((cur) => (cur === v.id ? null : cur));
    audio.onerror = () => setPlayingId((cur) => (cur === v.id ? null : cur));
    audio.play().catch(() => {
      setPlayingId((cur) => (cur === v.id ? null : cur));
    });
  };

  return (
    <div>
      {playedFallback && (
        <p className="mb-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-[10px] px-3 py-2">
          {engine === 'openai'
            ? 'Voice preview is unavailable right now (check the OpenAI API key). The voice still applies on real calls.'
            : 'Playing a stock sample — live previews with your slider settings are unavailable right now (check the ElevenLabs API key). Sliders still apply on real calls.'}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {voicesForGender(gender, engine).map((v) => {
        const selected = voiceId === v.id;
        const isPlaying = playingId === v.id;
        const isLoading = loadingId === v.id;
        return (
          <div
            key={v.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(v.id)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(v.id)}
            className={`flex items-center gap-3 p-3 rounded-[12px] border cursor-pointer transition-colors ${
              selected
                ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-500/30'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                togglePlay(v);
              }}
              aria-label={isPlaying ? `Stop ${v.name} preview` : `Play ${v.name} preview`}
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                isPlaying || isLoading
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4 ml-0.5" />
              )}
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-gray-900 leading-tight">{v.name}</p>
              <p className="text-[12px] text-gray-500 truncate">{v.tagline}</p>
            </div>
            {selected && (
              <span className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Check className="h-3 w-3 text-white" />
              </span>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}

function TestTab({ agentId }: { agentId: string }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      setBusy(true);
      try {
        const { reply } = await simulateAgentTurn(agentId, []);
        setMessages([{ role: 'assistant', text: reply }]);
      } catch (err) {
        console.error(err);
        setError('Could not start the test conversation.');
      } finally {
        setBusy(false);
      }
    })();
  }, [agentId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setError(null);
    const next: ChatMessage[] = [...messages, { role: 'user', text }];
    setMessages(next);
    setBusy(true);
    try {
      const { reply } = await simulateAgentTurn(agentId, next);
      setMessages([...next, { role: 'assistant', text: reply }]);
    } catch (err) {
      console.error(err);
      setError('The receptionist could not respond. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] flex flex-col" style={{ height: '60vh' }}>
      <div className="px-4 py-3 border-b border-gray-200/60">
        <p className="text-sm text-gray-600">
          This is exactly how the receptionist behaves on a real call — same brain, in text.
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-[15px] leading-relaxed ${
                m.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-md'
                  : 'bg-gray-100 text-gray-900 rounded-bl-md'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-3.5 py-2.5 rounded-2xl rounded-bl-md">
              <Loader2 className="h-4 w-4 text-gray-500 animate-spin" />
            </div>
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div ref={bottomRef} />
      </div>
      <div className="p-3 border-t border-gray-200/60 flex gap-2">
        <input
          className="flex-1 rounded-[10px] border border-gray-300 px-3.5 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
          placeholder="Say something as a caller…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
        />
        <button
          onClick={send}
          disabled={busy || !input.trim()}
          className="px-4 rounded-[10px] bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700 transition-colors"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ActivityTab({ agentId }: { agentId: string }) {
  const [sessions, setSessions] = useState<AgentCallSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setSessions(await listAgentSessions(agentId));
      } catch (err) {
        console.error('Failed to load sessions:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [agentId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200/60 p-10 text-center shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
        <div className="w-14 h-14 rounded-[12px] bg-blue-100 flex items-center justify-center mx-auto mb-3">
          <PhoneCall className="h-6 w-6 text-blue-600" />
        </div>
        <h3 className="text-[16px] font-semibold text-gray-900 mb-1">No calls yet</h3>
        <p className="text-sm text-gray-600">
          Once the receptionist is live and answers a call, it shows up here with a transcript and summary.
        </p>
      </div>
    );
  }

  const outcomeLabel: Record<string, string> = {
    message_taken: 'Message taken',
    question_answered: 'Question answered',
    booking_requested: 'Booking requested',
    no_engagement: 'No engagement',
  };

  return (
    <div className="space-y-2.5">
      {sessions.map((s) => {
        const isOpen = expanded === s.id;
        return (
          <div key={s.id} className="bg-white rounded-2xl border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
            <button
              className="w-full text-left p-4 flex items-center justify-between gap-3"
              onClick={() => setExpanded(isOpen ? null : s.id)}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[15px] font-semibold text-gray-900">
                    {s.collected?.name || s.from_number || 'Unknown caller'}
                  </span>
                  {s.outcome && (
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">
                      {outcomeLabel[s.outcome] || s.outcome}
                    </span>
                  )}
                  {s.context_error && (
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
                      Lookup issue
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 truncate mt-0.5">
                  {s.summary || 'Call in progress or no summary yet.'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(s.started_at).toLocaleString()}
                  {s.duration_seconds != null && ` · ${Math.max(1, Math.round(s.duration_seconds / 60))} min`}
                </p>
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
              )}
            </button>
            {isOpen && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-2">
                {s.context_error && (
                  <div className="rounded-[10px] bg-amber-50 border border-amber-200 px-3 py-2">
                    <p className="text-[13px] font-semibold text-amber-800">Caller lookup issue on this call</p>
                    <p className="text-[13px] text-amber-700 mt-0.5 leading-snug">
                      The receptionist answered without some or all caller context (customer record, bookings, proposals). Details: {s.context_error}
                    </p>
                  </div>
                )}
                {(s.transcript || []).map((t, i) => (
                  <p key={i} className="text-sm leading-relaxed">
                    <span className={`font-semibold ${t.role === 'user' ? 'text-gray-900' : 'text-blue-700'}`}>
                      {t.role === 'user' ? 'Caller: ' : 'Receptionist: '}
                    </span>
                    <span className="text-gray-700">{t.text}</span>
                  </p>
                ))}
                {(!s.transcript || s.transcript.length === 0) && (
                  <p className="text-sm text-gray-500">No transcript recorded.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
